import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Copy } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Modal } from '../../../components/Shared/Modal';
import { Button } from '../../../components/Shared/Button';
import type { CreateTenantResponse } from '../../../store/useAdminTenantStore';

interface Props {
  response: CreateTenantResponse | null;
  onClose: () => void;
}

export function TenantCreatedDialog({ response, onClose }: Props) {
  const { t } = useTranslation(['admin', 'common']);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!response) setCopied(false);
  }, [response]);

  if (!response) return null;

  const email = response.adminEmail ?? '';
  const password = response.initialPassword ?? '';

  const handleCopy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may be blocked (insecure context); user can still
      // select & copy manually from the readonly input.
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('admin:tenants.created.title')}
      size="md"
      footer={
        <Button onClick={onClose}>
          {t('admin:tenants.created.actions.close')}
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-[13px] text-slate-700">
            {email
              ? t('admin:tenants.created.welcomeSent', { email })
              : t('admin:tenants.created.welcomeSentNoEmail')}
          </p>
        </div>

        {password && (
          <>
            <p className="text-[13px] text-slate-600">
              {t('admin:tenants.created.fallbackIntro')}
            </p>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
              {email && (
                <div className="grid grid-cols-[80px_1fr] items-center gap-2">
                  <span className="text-[12px] text-slate-500">
                    {t('admin:tenants.created.fields.email')}
                  </span>
                  <span className="font-mono text-[13px] text-slate-900 break-all">
                    {email}
                  </span>
                </div>
              )}
              <div className="grid grid-cols-[80px_1fr_auto] items-center gap-2">
                <span className="text-[12px] text-slate-500">
                  {t('admin:tenants.created.fields.password')}
                </span>
                <input
                  type="text"
                  readOnly
                  value={password}
                  onFocus={(e) => e.currentTarget.select()}
                  className="font-mono text-[13px] text-slate-900 bg-white border border-slate-200 rounded-md px-2 py-1 w-full"
                />
                <Button
                  variant="secondary"
                  onClick={handleCopy}
                  leftIcon={<Copy className="w-3.5 h-3.5" strokeWidth={2} />}
                >
                  {copied
                    ? t('admin:tenants.created.actions.copied')
                    : t('admin:tenants.created.actions.copy')}
                </Button>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="text-[12px] text-amber-900">
                {t('admin:tenants.created.warning')}
              </p>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default TenantCreatedDialog;
